import React, { useState, useEffect, useCallback } from "react";
import { getAdminGLAccounts, updateGLAccount, createGLAccount, deleteGLAccount } from "../../adminApi";
import type { GLAccount } from "../../adminTypes";

const ACCOUNT_TYPES = ["EXPENSE", "ASSET", "LIABILITY", "REVENUE", "EQUITY"];

const GLAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<GLAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<GLAccount>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<Partial<GLAccount>>({
    gl_number: "", description: "", category: "", account_type: "EXPENSE",
    vendor_specific: false, vendor_number: null,
  });
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminGLAccounts();
      setAccounts(data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load GL accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const categories = Array.from(new Set(accounts.map((a) => a.category).filter(Boolean))) as string[];

  const filtered = accounts.filter((a) => {
    const matchSearch =
      !search ||
      a.gl_number.toLowerCase().includes(search.toLowerCase()) ||
      a.description.toLowerCase().includes(search.toLowerCase());
    const matchCategory = !categoryFilter || a.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const activeCount = accounts.filter((a) => a.active).length;

  const handleExpand = (acc: GLAccount) => {
    if (expandedId === acc.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(acc.id);
    setEditForm({
      description: acc.description,
      category: acc.category || "",
      account_type: acc.account_type,
      vendor_specific: acc.vendor_specific,
      vendor_number: acc.vendor_number,
      active: acc.active,
    });
  };

  const handleSave = async () => {
    if (!expandedId) return;
    setSaving(true);
    try {
      await updateGLAccount(expandedId, editForm);
      setToast({ type: "success", message: "GL account updated" });
      setExpandedId(null);
      fetchAccounts();
    } catch (err: any) {
      setToast({ type: "error", message: err?.response?.data?.error || "Failed to update" });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    setAddError("");
    if (!addForm.gl_number?.trim() || !addForm.description?.trim()) {
      setAddError("GL Number and Description are required.");
      return;
    }
    setAddSaving(true);
    try {
      await createGLAccount(addForm);
      setToast({ type: "success", message: "GL account created" });
      setShowAddModal(false);
      setAddForm({ gl_number: "", description: "", category: "", account_type: "EXPENSE", vendor_specific: false, vendor_number: null });
      fetchAccounts();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setAddError(`GL number '${addForm.gl_number}' already exists.`);
      } else {
        setAddError(err?.response?.data?.error || "Failed to create GL account.");
      }
    } finally {
      setAddSaving(false);
    }
  };

  const handleDeactivate = async (acc: GLAccount) => {
    if (!window.confirm(`Deactivate GL account ${acc.gl_number} – ${acc.description}?`)) return;
    setDeleting(acc.id);
    try {
      await deleteGLAccount(acc.id);
      setToast({ type: "success", message: `GL account ${acc.gl_number} deactivated` });
      setExpandedId(null);
      fetchAccounts();
    } catch (err: any) {
      setToast({ type: "error", message: err?.response?.data?.error || "Failed to deactivate" });
    } finally {
      setDeleting(null);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────
  const badgeStyle = (color: string, bg: string): React.CSSProperties => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 10,
    fontSize: 11, fontWeight: 600, color, background: bg,
  });
  const activeDotStyle = (active: boolean): React.CSSProperties => ({
    display: "inline-block", width: 8, height: 8, borderRadius: "50%",
    background: active ? "#2d7a2d" : "#ccc", marginRight: 6,
  });
  const toastStyle = (type: string): React.CSSProperties => ({
    position: "fixed" as const, top: 16, right: 16, padding: "12px 20px",
    borderRadius: 6, color: "white", fontWeight: 500, zIndex: 9999,
    background: type === "success" ? "#2d7a2d" : "#E33126",
  });

  const styles: Record<string, React.CSSProperties> = {
    container: { padding: "0" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
    title: { fontSize: 18, fontWeight: 600, color: "#00386C", margin: 0 },
    stats: { fontSize: 13, color: "#666" },
    filterRow: { display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" },
    searchInput: {
      padding: "8px 12px", border: "1px solid #ddd", borderRadius: 4,
      fontSize: 14, width: 260, outline: "none",
    },
    select: {
      padding: "8px 12px", border: "1px solid #ddd", borderRadius: 4,
      fontSize: 14, background: "white", outline: "none",
    },
    table: { width: "100%", borderCollapse: "collapse" as const, fontSize: 14 },
    th: {
      background: "#00386C", color: "white", padding: "10px 12px",
      textAlign: "left" as const, fontWeight: 600, fontSize: 13,
    },
    td: { padding: "10px 12px", borderBottom: "1px solid #eee" },
    row: { cursor: "pointer", transition: "background 0.15s" },
    detailPanel: {
      background: "#f8f9fa", padding: 20, borderBottom: "2px solid #00386C",
    },
    formGrid: {
      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 600,
    },
    formGroup: { display: "flex", flexDirection: "column" as const, gap: 4 },
    label: { fontSize: 12, fontWeight: 600, color: "#555" },
    input: {
      padding: "8px 10px", border: "1px solid #ddd", borderRadius: 4,
      fontSize: 14, outline: "none",
    },
    checkbox: { display: "flex", alignItems: "center", gap: 8 },
    saveBtn: {
      marginTop: 16, padding: "8px 24px", background: "#00386C", color: "white",
      border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600,
      opacity: saving ? 0.6 : 1,
    },
    spinner: { textAlign: "center" as const, padding: 40, color: "#666" },
    empty: { textAlign: "center" as const, padding: 40, color: "#999" },
  };

  if (loading) return <div style={styles.spinner}>Loading GL accounts...</div>;
  if (error) return <div style={{ ...styles.spinner, color: "#E33126" }}>{error}</div>;

  return (
    <div style={styles.container}>
      {toast && <div style={toastStyle(toast.type)}>{toast.message}</div>}

      <div style={styles.header}>
        <h3 style={styles.title}>GL Accounts</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={styles.stats}>{activeCount} active / {accounts.length} total</span>
          <button
            onClick={() => { setShowAddModal(true); setAddError(""); }}
            style={{
              padding: "7px 16px", background: "#00386C", color: "white",
              border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: 13,
            }}
          >
            + Add GL Account
          </button>
        </div>
      </div>

      <div style={styles.filterRow}>
        <input
          style={styles.searchInput}
          placeholder="Search by GL number or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={styles.select} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.sort().map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>GL Number</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Account Type</th>
            <th style={styles.th}>Vendor Specific</th>
            <th style={styles.th}>Active</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={styles.empty}>No GL accounts match your filters</td></tr>
          )}
          {filtered.map((acc) => (
            <React.Fragment key={acc.id}>
              <tr
                style={{
                  ...styles.row,
                  background: expandedId === acc.id ? "#e8f0fe" : "white",
                }}
                onClick={() => handleExpand(acc)}
                onMouseEnter={(e) => { if (expandedId !== acc.id) e.currentTarget.style.background = "#f5f5f5"; }}
                onMouseLeave={(e) => { if (expandedId !== acc.id) e.currentTarget.style.background = "white"; }}
              >
                <td style={{ ...styles.td, fontWeight: 600, fontFamily: "monospace" }}>{acc.gl_number}</td>
                <td style={styles.td}>{acc.description}</td>
                <td style={styles.td}>
                  {acc.category ? (
                    <span style={badgeStyle("#00386C", "#e0ecf5")}>{acc.category}</span>
                  ) : (
                    <span style={{ color: "#999" }}>—</span>
                  )}
                </td>
                <td style={styles.td}>{acc.account_type}</td>
                <td style={styles.td}>
                  {acc.vendor_specific ? (
                    <span style={badgeStyle("#8b5cf6", "#ede9fe")}>
                      Vendor {acc.vendor_number}
                    </span>
                  ) : (
                    <span style={{ color: "#999" }}>No</span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={activeDotStyle(acc.active)} />
                  {acc.active ? "Active" : "Inactive"}
                </td>
              </tr>
              {expandedId === acc.id && (
                <tr>
                  <td colSpan={6} style={styles.detailPanel}>
                    <div style={styles.formGrid}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Description</label>
                        <input
                          style={styles.input}
                          value={editForm.description || ""}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Category</label>
                        <input
                          style={styles.input}
                          value={editForm.category || ""}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Account Type</label>
                        <select
                          style={styles.select}
                          value={editForm.account_type || "EXPENSE"}
                          onChange={(e) => setEditForm({ ...editForm, account_type: e.target.value })}
                        >
                          {ACCOUNT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>&nbsp;</label>
                        <div style={styles.checkbox}>
                          <input
                            type="checkbox"
                            checked={editForm.vendor_specific || false}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                vendor_specific: e.target.checked,
                                vendor_number: e.target.checked ? editForm.vendor_number : null,
                              })
                            }
                          />
                          <span style={{ fontSize: 14 }}>Vendor Specific</span>
                        </div>
                      </div>
                      {editForm.vendor_specific && (
                        <div style={styles.formGroup}>
                          <label style={styles.label}>Vendor Number</label>
                          <input
                            style={styles.input}
                            type="number"
                            value={editForm.vendor_number || ""}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                vendor_number: e.target.value ? parseInt(e.target.value) : null,
                              })
                            }
                          />
                        </div>
                      )}
                      <div style={styles.formGroup}>
                        <label style={styles.label}>&nbsp;</label>
                        <div style={styles.checkbox}>
                          <input
                            type="checkbox"
                            checked={editForm.active ?? true}
                            onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                          />
                          <span style={{ fontSize: 14 }}>Active</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                      <button style={{ ...styles.saveBtn, marginTop: 0 }} onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                      {acc.active && (
                        <button
                          onClick={() => handleDeactivate(acc)}
                          disabled={deleting === acc.id}
                          style={{
                            padding: "8px 18px", background: "#fff0ee", color: "#c0392b",
                            border: "1px solid #fca5a5", borderRadius: 4, cursor: "pointer",
                            fontWeight: 600, fontSize: 14, opacity: deleting === acc.id ? 0.6 : 1,
                          }}
                        >
                          {deleting === acc.id ? "Deactivating..." : "Deactivate"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {/* Add GL Account Modal */}
      {showAddModal && (
        <div style={{
          position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: 8, padding: 28,
            width: 460, maxWidth: "95vw", boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          }}>
            <h3 style={{ margin: "0 0 20px 0", fontSize: 16, fontWeight: 600, color: "#00386C" }}>
              Add GL Account
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>GL Number *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. 4136"
                  value={addForm.gl_number || ""}
                  onChange={(e) => setAddForm({ ...addForm, gl_number: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Account Type</label>
                <select
                  style={styles.select}
                  value={addForm.account_type || "EXPENSE"}
                  onChange={(e) => setAddForm({ ...addForm, account_type: e.target.value })}
                >
                  {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Description *</label>
                <input
                  style={styles.input}
                  placeholder="e.g. F&N - Miscellaneous"
                  value={addForm.description || ""}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Food & Nutrition"
                  value={addForm.category || ""}
                  onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                />
              </div>
              <div style={{ ...styles.formGroup, justifyContent: "flex-end" }}>
                <label style={styles.label}>&nbsp;</label>
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={addForm.vendor_specific || false}
                    onChange={(e) => setAddForm({
                      ...addForm,
                      vendor_specific: e.target.checked,
                      vendor_number: e.target.checked ? addForm.vendor_number : null,
                    })}
                  />
                  <span style={{ fontSize: 14 }}>Vendor Specific</span>
                </div>
              </div>
              {addForm.vendor_specific && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Vendor Number</label>
                  <input
                    style={styles.input}
                    type="number"
                    value={addForm.vendor_number || ""}
                    onChange={(e) => setAddForm({
                      ...addForm,
                      vendor_number: e.target.value ? parseInt(e.target.value) : null,
                    })}
                  />
                </div>
              )}
            </div>

            {addError && (
              <div style={{ color: "#c0392b", fontSize: 13, marginTop: 10 }}>{addError}</div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button
                onClick={() => setShowAddModal(false)}
                disabled={addSaving}
                style={{
                  padding: "8px 18px", background: "white", color: "#555",
                  border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={addSaving}
                style={{
                  padding: "8px 20px", background: "#00386C", color: "white",
                  border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600,
                  fontSize: 14, opacity: addSaving ? 0.6 : 1,
                }}
              >
                {addSaving ? "Saving..." : "Add GL Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GLAccounts;
